import { Form, Input } from 'antd';
import { CSSProperties, useEffect, useState } from 'react';
import { ExistingTaskInstruction, PimsUser } from '../../utils/dataTypes';
import { initializeForm } from '../../hooks/useOnFormInitialize';
import { TaskSearchOption } from '../../utils/types/InstructionOrderTypes';
import {
	MedicationEnd,
	MedicationFrequency,
	MedicationNote,
	MedicationStart,
} from './MedicineOrder';
import { checkIfSerialCheaper, next30Min } from 'utils/formFuncs';
import moment from 'moment';
import { BASE_QUERY_OPTIONS, DEFAULT_INSTRUCTION_LENGTH_MINUTES, INSTRUCTION_FREQUENCY_REGEX } from 'utils/constants';
import { HiddenInput } from './fields/HiddenInput';
import { useGetUserDataQuery } from 'services/authService';
import { TaskOrderRules } from 'utils/types/validations';
import { endTimeGreaterThanStartTime } from 'utils/validationFuncs';
import AlertSerialPrice from 'components/alerts/AlertSerialPrice';

const formItemStyle: CSSProperties = {
	width: '100%',
};

const getDefaults = (
	options: TaskSearchOption,
	existingInstruction?: Partial<ExistingTaskInstruction>,
	user?: PimsUser,
): Partial<ExistingTaskInstruction> => {
	if (existingInstruction) {
		return {
			...existingInstruction,
			ordered_by: user?.user_id || 0,
		};
		;
	} else {
		const now = moment();
		const additionalTime = options.default_duration_mins;
		const startTime = next30Min(now).unix();
		const endTime = additionalTime
			? moment.unix(startTime).add(additionalTime, 'minutes').unix()
			: undefined;
		const defaultInstruction: Partial<ExistingTaskInstruction> = {
			task_id: options.id,
			type_id: options.type_id,
			name: options.name,
			frequency: options?.default_frequency || undefined,
			start_time: startTime,
			end_time: endTime,
			ordered_by: user?.user_id || 0,
		};
		return defaultInstruction;
	}
};


export const OUTSTANDING_ORDER_DATE_FORMAT = 'YYYY-MM-DD, hh:mm a';
interface TaskOrderProps {
	options: TaskSearchOption;
	existingInstruction?: Partial<ExistingTaskInstruction>;
	onFormChange?: Function; //not actually optional -- always sent via FormWrapper
    getFormData?: Function; //not actually optional -- always sent via FormWrapper
	labelCol?: any; //Width of LABEL, in "columns" (like <Col span={4})
	wrapperCol?: any; //Width of INPUT, in "columns" (like <Col span={12})
	fromEstimate?: boolean
}
export const TaskOrder = (props: TaskOrderProps) => {
	const { options, existingInstruction, fromEstimate } = props;

	const labelCol = props.labelCol || { span: 6 };
	const wrapperCol = props.wrapperCol || { span: 18 };
	const { data: loggedInUserData } = useGetUserDataQuery(
		null,
		BASE_QUERY_OPTIONS,
	);

    const [isSerialCheaper, setIsSerialCheaper] = useState(false)

	const onFormChange = (changes: any) => {     
        let serial = false   
        if (props?.getFormData){
            const fields = {
                ...props?.getFormData(),
                ...changes
            };
            serial = checkIfSerialCheaper(options, fields.frequency, fields?.start_time?.unix(), fields?.end_time?.unix())
            setIsSerialCheaper(serial)
        }
        if (props?.onFormChange){ 
            return props?.onFormChange({...changes, serial})
        } else {
            return (() => console.error('ERROR, onFormChange NOT PASSED THROUGH'));
        }
    }
	useEffect(() => {
		const defaultTask = getDefaults(options, existingInstruction, loggedInUserData);
		initializeForm(defaultTask, onFormChange);
	}, [options, existingInstruction]);


	const taskOrderRules: TaskOrderRules = {
		frequency: [{ required: true, pattern: INSTRUCTION_FREQUENCY_REGEX, }],
		start_time: [{ required: true, type: "date" }],
		end_time: [
			{ type: "date" },
			endTimeGreaterThanStartTime
		],
		notes: [],
	}
	return (
		<>
            {isSerialCheaper && <AlertSerialPrice serialHours={options.serial_hours ?? 0} inForm />}
			<MedicationFrequency
                autoFocus
				labelCol={labelCol} //Width of LABEL, in "columns" (like <Col span={4})
				wrapperCol={wrapperCol} //Width of INPUT, in "columns" (like <Col span={12})
				disabled={false}
				rules={taskOrderRules.frequency}
                onFormChange={onFormChange}

			/>
			<MedicationStart
				labelCol={labelCol} //Width of LABEL, in "columns" (like <Col span={4})
				wrapperCol={wrapperCol} //Width of INPUT, in "columns" (like <Col span={12})
				onFormChange={onFormChange} // Only needed because we default a value for this field
				rules={taskOrderRules.start_time}
			/>
			<MedicationEnd
				labelCol={labelCol} //Width of LABEL, in "columns" (like <Col span={4})
				wrapperCol={wrapperCol} //Width of INPUT, in "columns" (like <Col span={12})
				onFormChange={onFormChange} // Only needed because we default a value for this field
				rules={taskOrderRules.end_time}

			/>
			<MedicationNote
				labelCol={labelCol} //Width of LABEL, in "columns" (like <Col span={4})
				wrapperCol={wrapperCol} //Width of INPUT, in "columns" (like <Col span={12})
				rules={taskOrderRules.notes}

			/>

			<Form.Item
				preserve={false}
				hidden
				name='task_id'
				label='task_id'
				style={formItemStyle}
				labelCol={labelCol} //Width of LABEL, in "columns" (like <Col span={4})
				wrapperCol={wrapperCol} //Width of INPUT, in "columns" (like <Col span={12})
			>
				<Input disabled />
			</Form.Item>
			<HiddenInput
				fieldName={'ordered_by'}
			></HiddenInput>
			<HiddenInput
				fieldName={'id'}
			></HiddenInput>
			<HiddenInput
				fieldName={'priority'}
			></HiddenInput>
			<HiddenInput fieldName="userId" initialValue={loggedInUserData?.user_id} />
			<HiddenInput fieldName="fromEstimate" initialValue={fromEstimate} />
            <HiddenInput fieldName="serial" />
			{/* TODO Look at a simpler way of doing this, this code is copied is CriOrder, MedicineOrder, FluidOrder, and DiagnosticOrder */}
		</>
	);
};
